import { useFlag } from '@unleash/proxy-client-react'
import { useEffect, useRef, useState } from 'react'
import SingleCombobox from '@/components/common/single-combobox'
import { toast } from '@/hooks/use-toast'
import { useCurrentMedication } from '@/hooks/useCurrentMedication'
import { useQuestionnaire } from '@/hooks/useQuestionnaire'
import {
  isMultiProductOrderTreatmentComplete,
  type TreatmentDecisionInput
} from '@/lib/api/med-requests/multi-product-order/family-actions'
import { multiProductOrderTreatmentRequestsOf } from '@/lib/med-requests'
import { toMedicationOption } from '@/lib/medication-options'
import { canSendMessages } from '@/lib/permissions'
import { useMedicationRequestStore } from '@/lib/stores/medication-request'
import {
  checkApprovalJustificationLength,
  checkMedicationDirectionsLength,
  cn,
  isMultiProductOrderMedRequest
} from '@/lib/utils'
import { usePatientFilesGate } from '@/services/patientFiles'
import type { MedRequest } from '@/types/data'
import TextareaWithLabel from '../../textarea'
import { CSTeamView, PatientView } from '../contact-patient-section'

type Audience = 'patient' | 'cs-team'

const NOTICE_CLASSES = 'py-4 px-6 bg-[#FFF9E6] rounded-3xl border border-[#F4D03F] mb-4'

/** Shown once a card's Approve / Reject picks a treatment, unless a footer sidebar replaced the menu. */
export const useIsClinicalDecisionPanelOpen = (medRequest: MedRequest, isEditable: boolean) => {
  const isOpen = useMedicationRequestStore(state => state.sidebarView === 'navigation-menu' && !!state.decisionPanelTarget)
  return isOpen && isEditable && isMultiProductOrderMedRequest(medRequest)
}

// Remounts per treatment and action, so each decision starts with a fresh form.
export default function ClinicalDecisionPanel() {
  const decisionPanelTarget = useMedicationRequestStore(state => state.decisionPanelTarget)
  return <ClinicalDecisionForm key={`${decisionPanelTarget?.treatmentId}-${decisionPanelTarget?.action}`} />
}

/**
 * Opened by a treatment card's Approve / Reject; records that treatment's decision on the card for
 * Submit decision to send. Rejecting reuses the rejection sidebar's Patient and CS team views.
 */
function ClinicalDecisionForm() {
  const decisionPanelTarget = useMedicationRequestStore(state => state.decisionPanelTarget)
  const setDecisionPanelTarget = useMedicationRequestStore(state => state.setDecisionPanelTarget)
  const setRejectionReason = useMedicationRequestStore(state => state.setRejectionReason)
  const setTreatmentDecision = useMedicationRequestStore(state => state.setTreatmentDecision)
  const medication = useMedicationRequestStore(state => state.medication)
  const medicationDirection = useMedicationRequestStore(state => state.medicationDirection)
  const multiProductOrderTreatments = useMedicationRequestStore(state => state.multiProductOrderTreatments)

  const medRequest = useCurrentMedication()
  const data = medRequest?.data

  const treatmentRequests = data ? multiProductOrderTreatmentRequestsOf(data) : []
  const index = treatmentRequests.findIndex(row => row.id === decisionPanelTarget?.treatmentId)
  const target = treatmentRequests[index]

  // As in the rejection sidebar: the Patient tab shows on the flag or an existing rejection conversation.
  const isRejectionFanEnabled = useFlag('rejection-fan') || !!target?.rejectionConversationId
  const canSendMessagesRole = canSendMessages()
  const filesGate = usePatientFilesGate(data?.patient?.id)

  const [audience, setAudience] = useState<Audience>(isRejectionFanEnabled ? 'patient' : 'cs-team')
  const [template, setTemplate] = useState('')
  const [reason, setReason] = useState('')
  const [prompt, setPrompt] = useState('')
  const [isGenerationViewActive, setIsGenerationViewActive] = useState(false)
  const [aiResponse, setAiResponse] = useState('')

  // The rejection views share the store message with the sidebar, so it's cleared on close.
  useEffect(() => () => setRejectionReason(''), [setRejectionReason])

  // Scrolls the right column, not the page, to bring the panel to its top.
  const panelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const panel = panelRef.current
    panel?.parentElement?.scrollTo({ top: panel.offsetTop })
  }, [])

  const { templateOptions } = useQuestionnaire(target?.questionaire ?? '')

  if (!data || !decisionPanelTarget || !target) return null

  const isApprove = decisionPanelTarget.action === 'approve'
  const isOpened = target.id === data.id
  const treatmentState = isOpened
    ? { medication, direction: medicationDirection }
    : multiProductOrderTreatments.find(treatment => treatment.id === target.id)
  const medicationLabel = treatmentState?.medication
    ? toMedicationOption(treatmentState.medication).label
    : target.orderedItem?.name || target.orderMedNameString
  const isOtc = treatmentState?.medication?.type === 'OTC'
  const position = index + 1

  const isApproveDisabled = !canSendMessagesRole || filesGate.isBlocked || (!isOtc && !reason.trim())

  const handleCancel = () => setDecisionPanelTarget(null)

  // Recorded on the card for Submit decision to send; the panel closes once it's recorded.
  const record = (input: TreatmentDecisionInput) => {
    setTreatmentDecision(decisionPanelTarget.treatmentId, { action: decisionPanelTarget.action, ...input })
    setDecisionPanelTarget(null)
  }

  const handleApprove = () => {
    if (isApproveDisabled) return

    if (!isMultiProductOrderTreatmentComplete(treatmentState)) {
      toast({
        title: 'Approval not recorded',
        description: `Treatment ${position} needs a medication and directions before it can be approved.`
      })
      return
    }

    if (!checkMedicationDirectionsLength(treatmentState?.direction ?? '')) return
    if (!checkApprovalJustificationLength(reason.trim())) return

    record({ reason: reason.trim() })
  }

  return (
    <div
      ref={panelRef}
      data-testid='clinical-decision-panel'
      className='mt-4 min-w-[25vw] max-w-[300px] overflow-hidden rounded-2xl border border-custom-neutral-200 bg-white shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'
    >
      <div className='bg-[#153CB7] px-6 py-4'>
        <p className='text-white text-xl font-semibold leading-[133.4%]'>Clinical decision</p>
      </div>

      <div className='p-4'>
        <div
          data-testid='clinical-decision-treatment'
          className='rounded-2xl border border-[#D6DEF5] bg-[#EEF1FB] px-4 py-3'
        >
          <div className='flex items-center gap-3'>
            <span className='flex size-7 shrink-0 items-center justify-center rounded-full bg-[#153CB7] text-sm font-semibold leading-none text-white'>
              {position}
            </span>
            <p className='text-[#1D1D1D] text-base font-medium leading-[170%]'>Treatment {position}</p>
          </div>
          <p className='mt-1 text-[#153CB7] text-xs font-semibold uppercase tracking-wider leading-[170%]'>
            Treatment {position} of {treatmentRequests.length}
          </p>
          <p className='text-[#1D1D1D] text-lg leading-[150%]'>{medicationLabel}</p>
        </div>

        {!isApprove && <div className='mt-4 h-px bg-custom-neutral-200' />}

        <p className='mt-6 text-[#1D1D1D] text-xl font-semibold leading-[133.4%]'>
          {isApprove ? 'Approval reason' : 'Reject reason'}
        </p>
        <p className='mb-4 text-custom-neutral-500 text-xs leading-[170%]'>
          {isApprove ? 'Provide a reason for approving the treatment' : 'Provide a reason for rejecting this treatment'}
        </p>

        {canSendMessagesRole && filesGate.isBlocked && (
          <div className={NOTICE_CLASSES}>
            <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
              All patient files must be viewed before deciding.
            </p>
          </div>
        )}

        {!isApprove && isRejectionFanEnabled && (
          <div className='mb-4 flex rounded-xl bg-[#F4F5F7] p-1' role='tablist'>
            {(['patient', 'cs-team'] as const).map(tab => (
              <button
                key={tab}
                type='button'
                role='tab'
                aria-selected={audience === tab}
                data-testid={`clinical-decision-tab-${tab}`}
                onClick={() => setAudience(tab)}
                className={cn(
                  'flex-1 rounded-lg py-2 text-sm leading-[170%] transition-colors duration-200 cursor-pointer',
                  audience === tab ? 'bg-white text-[#153CB7] font-medium shadow-sm' : 'text-custom-neutral-500'
                )}
              >
                {tab === 'patient' ? 'Patient' : 'CS team'}
              </button>
            ))}
          </div>
        )}

        {!isApprove &&
          (isRejectionFanEnabled && audience === 'patient' ? (
            <PatientView
              isRejection
              target={target}
              onDecide={record}
              onCancel={handleCancel}
              isSendDisabled={filesGate.isBlocked}
              prompt={prompt}
              setPrompt={setPrompt}
              isGenerationViewActive={isGenerationViewActive}
              setIsGenerationViewActive={setIsGenerationViewActive}
              aiResponse={aiResponse}
              setAiResponse={setAiResponse}
            />
          ) : (
            <CSTeamView
              isRejection
              target={target}
              onDecide={record}
              onCancel={handleCancel}
              isSendDisabled={filesGate.isBlocked}
            />
          ))}

        {isApprove && (
          <>
            {!canSendMessagesRole && (
              <div className={NOTICE_CLASSES}>
                <p className='text-sm leading-[170%] text-[#B7950B] font-normal'>
                  You have read-only access. You cannot approve medication requests.
                </p>
              </div>
            )}

            {!!templateOptions?.approvalOptions.length && (
              <>
                <p className='mb-2 text-[#1D1D1D] text-sm font-medium leading-[170%]'>Select a template</p>
                <div className='mb-4'>
                  <SingleCombobox
                    title='Templates'
                    isDisabled={!canSendMessagesRole}
                    defaultValue={template}
                    options={templateOptions.approvalOptions}
                    onValueChange={value => {
                      setTemplate(value)
                      setReason(value)
                    }}
                  />
                </div>
              </>
            )}

            <p className='mb-2 text-[#1D1D1D] text-sm font-medium leading-[170%]'>Reason</p>
            <TextareaWithLabel
              data-testid='clinical-decision-reason'
              className='h-[140px] rounded-2xl resize-none'
              value={reason}
              placeholder={isOtc ? 'Optional for OTC treatments' : 'Add your reason'}
              onChange={event => setReason(event.target.value)}
              disabled={!canSendMessagesRole}
            />

            <div className='mt-6 flex gap-4'>
              <button
                type='button'
                data-testid='clinical-decision-submit'
                disabled={isApproveDisabled}
                onClick={handleApprove}
                className={cn(
                  'flex flex-1 h-14 items-center justify-center gap-2 rounded-2xl bg-[#153CB7] text-white text-sm font-semibold leading-[170%] hover:bg-[#0F2E8A] transition-colors duration-200',
                  isApproveDisabled && 'opacity-50 cursor-not-allowed'
                )}
              >
                Submit
              </button>
              <button
                type='button'
                data-testid='clinical-decision-cancel'
                onClick={handleCancel}
                className='flex flex-1 h-14 items-center justify-center rounded-2xl border border-custom-neutral-500 bg-white text-[#153CB7] text-sm font-semibold leading-[170%] hover:bg-custom-neutral-200 transition-colors duration-200 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
